import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Robot, CheckCircle, XCircle, ArrowLeft, ArrowRight, ArrowsClockwise,
  Sparkle, PencilSimple, VideoCamera, Trophy, MagicWand, Keyboard,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import { useLang } from "../contexts/LanguageContext";
import { formatTitleHistoryEntry } from "../lib/knowledgeFormat";
import { KnowledgeDetailModal } from "../components/knowledge/KnowledgeDetailModal";

import { API_BASE as API } from "../lib/apiBase";

// Bogue 1403 (Eric 2026-09-30) — `question_type` est une VALEUR DU SERVEUR
// (« pertinente », « intéressante », « anodine »), affichée brute : c'est
// « Pertinente » qu'Eric lisait au milieu d'un écran anglais. Même famille
// que le libellé d'étape de 1378 — aucun dictionnaire du navigateur ne peut
// traduire ce qu'il reçoit comme donnée.
//
// Cette table vit HORS du composant : elle ne peut pas appeler le traducteur,
// les deux langues sont donc sur la même entrée (motif 1324b).
//
// Le repli rend la valeur TELLE QUELLE : un type ajouté au serveur doit
// rester visible, quitte à être en français, plutôt que de disparaître de la
// fiche sans que personne le remarque.
const LIBELLES_TYPE_QUESTION = {
  pertinente: ["Pertinente", "Relevant"],
  "intéressante": ["Intéressante", "Interesting"],
  anodine: ["Anodine", "Trivial"],
};

function libelleTypeQuestion(valeur, lang) {
  const paire = LIBELLES_TYPE_QUESTION[String(valeur || "").toLowerCase()];
  if (!paire) return valeur;
  return lang === "en" ? paire[1] : paire[0];
}

export default function AgentDraftReviewPage() {
  const navigate = useNavigate();
  const { lang, tr } = useLang();
  const [drafts, setDrafts] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  // Vincent 20/05/2026 — Unification UI : "Éditer Titre/Q/R" ouvre maintenant le MÊME
  // KnowledgeDetailModal que celui utilisé dans Validation des données, pour une expérience
  // d'édition identique peu importe le point d'entrée.
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [editedAnswer, setEditedAnswer] = useState("");
  const [editedQuestion, setEditedQuestion] = useState("");
  const [stats, setStats] = useState({ approved: 0, rejected: 0 });
  // Vincent bug #133 / #110 (Eric 2026-05-14) — Title-IA + history parity with
  // the inline Q&R card in VideoDetailPage. State per-current-draft.
  const [titleProposals, setTitleProposals] = useState([]);
  const [titleGenerating, setTitleGenerating] = useState(false);
  const [titleHistory, setTitleHistory] = useState([]);
  const [titleHistoryOpen, setTitleHistoryOpen] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeDir, setSwipeDir] = useState(null); // "left" | "right" | null
  // Vincent #142 (Eric 2026-05-16) — Highlight de la nouvelle carte pendant 0.5s
  // après chaque décision. Plutôt qu'un pivotement étrange, on fait disparaître
  // l'item validé et on surligne la suivante en jaune doux.
  const [highlightNext, setHighlightNext] = useState(false);
  // Vincent #158 (Eric 2026-05-22) — Animation fade au lieu de translate.
  const [fadeOut, setFadeOut] = useState(false);
  const startX = useRef(0);
  const isDragging = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/agent/draft-queue?limit=50`, { withCredentials: true });
      setDrafts(r.data.drafts || []);
      setIdx(0);
    } catch (e) {
      toast.error(tr("Erreur de chargement", "Loading failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const current = drafts[idx];

  const decide = async (decision) => {
    if (!current || busy) return;
    setBusy(true);
    // Vincent #158 (Eric 2026-05-22) — Animation modifiée : fade OUT (opacité)
    // au lieu de translate (qui faisait "sortir" la carte sur le côté).
    // Beaucoup plus subtil, ne donne plus l'impression que la carte est "jetée"
    // et masque mieux le re-render de la prochaine carte.
    setSwipeDir(decision === "approve" ? "right" : "left");
    setSwipeOffset(0); // <- pas de translate
    setFadeOut(true);
    try {
      const payload = { decision };
      if (decision === "approve" && editing) {
        if (editedTitle.trim()) payload.edited_title = editedTitle.trim();
        if (editedAnswer.trim()) payload.edited_answer = editedAnswer.trim();
        if (editedQuestion.trim()) payload.edited_question = editedQuestion.trim();
      }
      await axios.post(
        `${API}/api/agent/draft-queue/${current.id}/decide`,
        payload,
        { withCredentials: true }
      );
      setStats((s) => ({
        approved: decision === "approve" ? s.approved + 1 : s.approved,
        rejected: decision === "reject" ? s.rejected + 1 : s.rejected,
      }));
      // Vincent #142/#158 — Wait for fade-out (300ms), advance, then 1s yellow ring.
      setTimeout(() => {
        setFadeOut(false);
        setSwipeDir(null);
        setEditing(false);
        setEditedTitle("");
        setEditedAnswer("");
        setEditedQuestion("");
        setIdx((i) => i + 1);
        setBusy(false);
        // Vincent #158 — Surlignage jaune élargi à 1 seconde (plus visible).
        setHighlightNext(true);
        setTimeout(() => setHighlightNext(false), 1000);
      }, 300);
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur", "Error"));
      setFadeOut(false);
      setSwipeDir(null);
      setBusy(false);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (editing || editorModalOpen) return;
      if (e.key === "ArrowRight") { e.preventDefault(); decide("approve"); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); decide("reject"); }
      else if (e.key.toLowerCase() === "e" && current) {
        e.preventDefault();
        // Vincent 20/05/2026 — Ouvre le modal unifié au lieu de l'édition inline.
        setEditorModalOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, editing, busy, editorModalOpen]);

  // Vincent bug #133 (Eric 2026-05-14) — Title-IA generation parity with
  // VideoDetailPage. Generates 3 alternative titles via GPT-5.2 and shows
  // them as clickable chips. Resets when the user moves to another draft.
  useEffect(() => {
    setTitleProposals([]);
    setTitleHistory([]);
    setTitleHistoryOpen(false);
  }, [current?.id]);

  // Load the title-edit history of the current draft when entering edit mode.
  useEffect(() => {
    if (!editing || !current?.id) return;
    let cancelled = false;
    axios.get(`${API}/api/agent/knowledge-units/${current.id}/history`, { withCredentials: true })
      .then(({ data }) => {
        if (cancelled) return;
        const editHistory = data?.edit_history || [];
        const titleEntries = editHistory
          .filter((e) => (e.fields_changed || []).includes("title") && (e.previous_title || "").trim())
          .map((e) => ({ title: e.previous_title, replaced_at: e.edited_at, replaced_by_email: e.edited_by_email }));
        setTitleHistory(titleEntries.slice(-20));
      })
      .catch(() => { /* historique optionnel */ });
    return () => { cancelled = true; };
  }, [editing, current?.id]);

  const generateTitleProposals = async () => {
    if (!current?.id || titleGenerating) return;
    setTitleGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/agent/knowledge-units/${current.id}/generate-title`, {}, { withCredentials: true });
      setTitleProposals(data?.proposals || []);
    } catch (err) {
      toast.error(`Erreur génération titre : ${err?.response?.data?.detail || err.message}`);
    }
    setTitleGenerating(false);
  };

  const applyProposedTitle = (t) => {
    setEditedTitle((t || "").slice(0, 100));
  };

  const clearTitleHistory = async () => {
    if (!current?.id) return;
    const ok = await confirmDialog(tr("Effacer tout l'historique des titres précédents de cette Q&R ?", "Clear the whole history of previous titles for this Q&A?"));
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/agent/knowledge-units/${current.id}/title-history`, { withCredentials: true });
      setTitleHistory([]);
      toast.success(tr("Historique effacé", "History cleared"));
    } catch (err) {
      toast.error(`Erreur : ${err?.response?.data?.detail || err.message}`);
    }
  };

  // Touch/mouse swipe
  const onPointerDown = (e) => {
    if (busy || editing) return;
    isDragging.current = true;
    startX.current = e.touches ? e.touches[0].clientX : e.clientX;
  };
  const onPointerMove = (e) => {
    if (!isDragging.current) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const delta = x - startX.current;
    setSwipeOffset(delta);
    setSwipeDir(delta > 50 ? "right" : delta < -50 ? "left" : null);
  };
  const onPointerUp = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    if (swipeOffset > 120) decide("approve");
    else if (swipeOffset < -120) decide("reject");
    else { setSwipeOffset(0); setSwipeDir(null); }
  };

  const remaining = drafts.length - idx;
  const allDone = !loading && drafts.length > 0 && idx >= drafts.length;
  const noDrafts = !loading && drafts.length === 0;

  return (
    <div className="max-w-3xl mx-auto p-4 lg:p-6 space-y-6 select-none animate-fade-in">
      {/* Header — Vincent #133 (Eric 2026-05-16) — Harmonisé avec DataValidation:
          même taille d'icône (w-12 h-12 rounded-2xl), même couleur KNORIA, même
          font Outfit sur le titre. */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#001a5e] flex items-center justify-center">
            <MagicWand size={22} weight="duotone" className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold font-['Outfit'] flex items-center gap-2" data-testid="draft-review-title">
              {tr("File de validation rapide", "Quick review queue")}
              <Sparkle size={16} weight="fill" className="text-amber-400" />
            </h1>
            <p className="text-xs text-muted-foreground">
              {tr("Brouillons générés par l'Agent — validez en quelques secondes", "Drafts written by the Agent — review them in seconds")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1.5 bg-teal-500/10 text-teal-600 rounded-lg text-xs font-bold flex items-center gap-1" data-testid="stat-approved">
            <CheckCircle size={14} weight="fill" /> {stats.approved}
          </span>
          <span className="px-3 py-1.5 bg-red-500/10 text-red-600 rounded-lg text-xs font-bold flex items-center gap-1" data-testid="stat-rejected">
            <XCircle size={14} weight="fill" /> {stats.rejected}
          </span>
          <span className="px-3 py-1.5 bg-secondary rounded-lg text-xs font-semibold" data-testid="stat-remaining">
            {remaining > 0 ? remaining : 0} {tr(remaining > 1 ? "restants" : "restant", "left")}
          </span>
        </div>
      </div>

      {/* Card stack */}
      {loading ? (
        <div className="text-center py-20 text-muted-foreground text-sm">
          <ArrowsClockwise size={32} className="mx-auto mb-3 animate-spin opacity-50" />
          {tr("Chargement…", "Loading…")}
        </div>
      ) : noDrafts ? (
        <div className="text-center py-20 bg-card border border-border rounded-2xl">
          <Robot size={48} weight="duotone" className="text-muted-foreground/40 mx-auto mb-3" />
          <p className="font-semibold">{tr("Aucun brouillon en attente", "No draft waiting")}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {tr("Allez sur l'Agent pour générer des Q&R automatiquement à partir des questions mal notées.", "Go to the Agent to generate Q&A automatically from the poorly-rated questions.")}
          </p>
          <Button onClick={() => navigate("/dashboard/agent")} className="mt-4 gap-2" data-testid="btn-go-agent">
            <Robot size={16} /> {tr("Aller à l'Agent Knoria", "Go to the Knoria Agent")}
          </Button>
        </div>
      ) : allDone ? (
        <div className="text-center py-20 bg-gradient-to-br from-teal-500/10 to-teal-500/5 border border-teal-500/30 rounded-2xl">
          <Trophy size={56} weight="duotone" className="text-amber-500 mx-auto mb-3" />
          <p className="text-xl font-bold">{tr("Tout est traité ! 🎉", "All done! 🎉")}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {stats.approved} {tr("approuvés", "approved")} · {stats.rejected} {tr("rejetés", "rejected")}
          </p>
          <Button onClick={load} className="mt-4 gap-2" data-testid="btn-reload">
            <ArrowsClockwise size={16} /> {tr("Recharger la file", "Reload the queue")}
          </Button>
        </div>
      ) : current ? (
        <>
          <div className="relative h-[520px]" data-testid="draft-card-stack">
            {/* Background card hint (next item) */}
            {drafts[idx + 1] && (
              <div className="absolute inset-0 bg-card border border-border rounded-2xl scale-95 opacity-50 -z-0" />
            )}
            {/* Active card */}
            <div
              className={`absolute inset-0 bg-card border-2 rounded-2xl p-6 cursor-grab active:cursor-grabbing transition-all duration-300 shadow-xl ${
                swipeDir === "right" ? "border-teal-500" :
                swipeDir === "left" ? "border-red-500" :
                highlightNext ? "border-amber-400 ring-4 ring-amber-200/50" :
                "border-border"
              }`}
              style={{
                // Vincent #158 (Eric 2026-05-22) — Animation refondue : fade in/out
                // de l'opacité seulement (plus de translate qui faisait "voler" la
                // carte). Le translate ne s'applique qu'au drag manuel utilisateur.
                transform: `translateX(${swipeOffset}px)`,
                opacity: fadeOut ? 0 : Math.max(0, 1 - Math.abs(swipeOffset) / 600),
                transition: isDragging.current ? "none" : "transform 0.2s ease-out, opacity 0.3s ease-in-out, border-color 0.3s, box-shadow 0.3s",
              }}
              onMouseDown={onPointerDown}
              onMouseMove={onPointerMove}
              onMouseUp={onPointerUp}
              onMouseLeave={onPointerUp}
              onTouchStart={onPointerDown}
              onTouchMove={onPointerMove}
              onTouchEnd={onPointerUp}
              data-testid="draft-card"
            >
              {/* Swipe indicators */}
              {swipeDir === "right" && (
                <div className="absolute top-6 left-6 px-3 py-1.5 bg-teal-500 text-white rounded-lg font-black text-sm rotate-[-12deg] shadow-lg">
                  {tr("APPROUVER", "APPROVE")} ✓
                </div>
              )}
              {swipeDir === "left" && (
                <div className="absolute top-6 right-6 px-3 py-1.5 bg-red-500 text-white rounded-lg font-black text-sm rotate-[12deg] shadow-lg">
                  {tr("REJETER", "REJECT")} ✗
                </div>
              )}

              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {/* Vincent #143 (Eric 2026-05-16) — Badge type connaissance
                    en premier (Q&R / Procédure / Script / Objection) pour que
                    Vincent voie immédiatement de quoi il valide. */}
                {(() => {
                  const t = (current.type || current.content?.type || "qa").toLowerCase();
                  const meta = {
                    qa:        { label: lang === "fr" ? "Q&R" : "Q&A",        color: "bg-blue-500/15 text-blue-700 border border-blue-300", icon: "💬" },
                    procedure: { label: lang === "fr" ? "Procédure" : "Procedure", color: "bg-teal-500/15 text-teal-700 border border-teal-300", icon: "📋" },
                    script:    { label: lang === "fr" ? "Script de vente" : "Sales script", color: "bg-violet-500/15 text-violet-700 border border-violet-300", icon: "🎤" },
                    objection: { label: lang === "fr" ? "Objection" : "Objection", color: "bg-rose-500/15 text-rose-700 border border-rose-300", icon: "🛡" },
                    concept:   { label: lang === "fr" ? "Concept" : "Concept",  color: "bg-amber-500/15 text-amber-700 border border-amber-300", icon: "💡" },
                  }[t] || { label: t.charAt(0).toUpperCase() + t.slice(1), color: "bg-zinc-200 text-zinc-700 border border-zinc-300", icon: "📄" };
                  return (
                    <span
                      className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${meta.color}`}
                      data-testid="draft-type-badge"
                    >
                      <span className="mr-1">{meta.icon}</span>{meta.label}
                    </span>
                  );
                })()}
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-[#002FA7]/10 text-[#002FA7]">
                  {current.source === "agent_4prompt_pipeline"
                    ? tr("🧬 Pipeline 4 prompts", "🧬 4-prompt pipeline")
                    : tr("Brouillon Agent", "Agent draft")}
                </span>
                {(current.question_type || current.content?.question_type) && (
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${
                    (current.question_type || current.content?.question_type) === "pertinente" ? "bg-teal-500/10 text-teal-700" :
                    (current.question_type || current.content?.question_type) === "intéressante" ? "bg-amber-500/10 text-amber-700" :
                    "bg-violet-500/10 text-violet-700"
                  }`}>
                    {libelleTypeQuestion(
                      current.question_type || current.content?.question_type,
                      lang,
                    )}
                  </span>
                )}
                {(current.time_code || current.content?.time_code) && (
                  <span className="text-[10px] font-mono font-bold px-2 py-1 rounded bg-zinc-900 text-amber-400">
                    {current.time_code || current.content?.time_code}
                  </span>
                )}
                {current.confidence != null && (
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${
                    current.confidence >= 0.8 ? "bg-teal-500/10 text-teal-600" :
                    current.confidence >= 0.6 ? "bg-amber-500/10 text-amber-600" :
                    "bg-red-500/10 text-red-600"
                  }`}>
                    Confiance {Math.round(current.confidence * 100)}%
                  </span>
                )}
                {current.video_title && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-secondary text-muted-foreground flex items-center gap-1">
                    <VideoCamera size={10} /> {current.video_title.slice(0, 28)}
                  </span>
                )}
              </div>

              {/* Thème + categorization (Vincent's P1-P2 + P4) */}
              {(current.theme || current.content?.theme) && (
                <div className="flex items-center gap-2 mb-3 flex-wrap text-[11px]">
                  <span className="text-muted-foreground uppercase tracking-wider font-bold">{tr("Thème :", "Theme:")}</span>
                  <span className="font-semibold text-[#002FA7]">{current.theme || current.content?.theme}</span>
                  {(current.theme_category || current.content?.theme_category) && (
                    <>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-amber-600 font-semibold">{current.theme_category || current.content?.theme_category}</span>
                    </>
                  )}
                  {(current.theme_subcategory || current.content?.theme_subcategory) && (
                    <>
                      <span className="text-muted-foreground">›</span>
                      <span className="text-teal-600 font-semibold">{current.theme_subcategory || current.content?.theme_subcategory}</span>
                    </>
                  )}
                </div>
              )}

              {(current.keywords || current.content?.keywords) && (
                <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                  {(current.keywords || current.content?.keywords).split(",").map((k, ki) => (
                    <span key={ki} className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">
                      #{k.trim()}
                    </span>
                  ))}
                </div>
              )}

              {editing ? (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1 flex items-center justify-between">
                    <span>{tr("Titre", "Title")} <span className="text-muted-foreground/60">({editedTitle.length}/100)</span></span>
                    {/* Vincent bug #133 (Eric 2026-05-14) — Title-IA + history parity. */}
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={generateTitleProposals}
                        disabled={titleGenerating}
                        className="text-[10px] px-2 py-0.5 rounded bg-violet-100 text-violet-700 hover:bg-violet-200 disabled:opacity-50 flex items-center gap-1"
                        data-testid="quick-title-ia-btn"
                        title={tr("Générer 3 titres alternatifs via IA", "Generate 3 alternative titles with AI")}
                      >
                        <MagicWand size={10} /> {titleGenerating ? "…" : (lang === "fr" ? "Générer titre auto" : "Generate auto title")}
                      </button>
                      {titleHistory.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setTitleHistoryOpen((o) => !o)}
                          className="text-[10px] px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 hover:bg-zinc-200 flex items-center gap-1"
                          data-testid="quick-title-history-btn"
                          title={tr("Historique des titres précédents", "History of previous titles")}
                        >
                          🕐 {titleHistory.length}
                        </button>
                      )}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value.slice(0, 100))}
                    maxLength={100}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-base font-bold focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                    placeholder={tr("Titre court de la Q&R", "Short title for the Q&A")}
                    data-testid="textarea-edit-title"
                  />
                  {/* Proposals chips */}
                  {titleProposals.length > 0 && (
                    <div className="mt-2 space-y-1" data-testid="quick-title-proposals">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{tr("Propositions IA — cliquer pour appliquer :", "AI suggestions — click to apply:")}</div>
                      {titleProposals.map((p, pi) => (
                        <button
                          key={pi}
                          type="button"
                          onClick={() => applyProposedTitle(p)}
                          className="block w-full text-left text-xs px-2 py-1 rounded bg-violet-50 border border-violet-200 hover:bg-violet-100"
                          data-testid={`quick-title-proposal-${pi}`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* History dropdown */}
                  {titleHistoryOpen && titleHistory.length > 0 && (
                    <div className="mt-2 border border-zinc-200 rounded bg-white p-2 space-y-1 max-h-40 overflow-y-auto" data-testid="quick-title-history-list">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{tr("Anciens titres :", "Previous titles:")}</div>
                        <button
                          type="button"
                          onClick={clearTitleHistory}
                          className="text-[10px] text-rose-600 hover:underline"
                          data-testid="quick-title-history-clear"
                        >
                          🗑️ {tr("Effacer tout", "Clear all")}
                        </button>
                      </div>
                      {titleHistory.slice().reverse().map((h, hi) => (
                        <button
                          key={hi}
                          type="button"
                          onClick={() => { setEditedTitle((h.title || "").slice(0, 100)); setTitleHistoryOpen(false); }}
                          className="block w-full text-left text-xs px-2 py-1 rounded hover:bg-zinc-50"
                          data-testid={`quick-title-history-${hi}`}
                          title={lang === "fr" ? "Cliquez pour revenir à ce titre" : "Click to revert to this title"}
                        >
                          {/* Vincent 20/05/2026 — Format "AAAA-MM-JJ HH:MM : titre" */}
                          {h.replaced_at && (
                            <span className="text-zinc-500 font-mono text-[10px] mr-1">
                              {formatTitleHistoryEntry(h.replaced_at)} :
                            </span>
                          )}
                          <span>{h.title}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <h3 className="text-lg font-bold mb-3" data-testid="draft-title">{current.title}</h3>
              )}

              <div className="space-y-3 max-h-[340px] overflow-y-auto pr-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1 flex items-center justify-between">
                    <span>{tr("Question", "Question")}</span>
                    {!editing && (
                      <button
                        onClick={() => {
                          // Vincent 20/05/2026 — Ouvre le modal unifié au lieu de l'édition inline.
                          setEditorModalOpen(true);
                        }}
                        className="text-[10px] text-[#002FA7] hover:underline flex items-center gap-1"
                        data-testid="btn-edit-qa"
                      >
                        <PencilSimple size={10} /> {tr("Éditer Titre/Q/R (E)", "Edit title/Q/A (E)")}
                      </button>
                    )}
                  </div>
                  {editing ? (
                    <textarea
                      value={editedQuestion}
                      onChange={(e) => setEditedQuestion(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm italic focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                      data-testid="textarea-edit-question"
                    />
                  ) : (
                    <p className="text-sm italic text-muted-foreground" data-testid="draft-question">
                      {current.content?.question}
                    </p>
                  )}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1 flex items-center justify-between">
                    <span>{tr("Réponse", "Answer")}</span>
                  </div>
                  {editing ? (
                    <textarea
                      value={editedAnswer}
                      onChange={(e) => setEditedAnswer(e.target.value)}
                      rows={6}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                      autoFocus
                      data-testid="textarea-edit-answer"
                    />
                  ) : (
                    <p className="text-sm leading-relaxed" data-testid="draft-answer">
                      {current.content?.answer}
                    </p>
                  )}
                </div>
                {(current.content?.textual_proof) && (
                  <div className="bg-zinc-50 dark:bg-zinc-900 border-l-4 border-amber-500 rounded-r-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-amber-600 mb-1 flex items-center gap-1.5">
                      <Sparkle size={10} weight="fill" /> {tr("Preuve textuelle (verbatim)", "Textual proof (verbatim)")}
                    </div>
                    <p className="text-xs italic leading-relaxed font-mono text-zinc-700 dark:text-zinc-300" data-testid="draft-proof">
                      {current.content.textual_proof}
                    </p>
                    {current.video_id && (current.time_code || current.content?.time_code) && (
                      <button
                        onClick={() => {
                          // Parse [HH:MM] or [HH:MM:SS] → seconds
                          const tc = current.time_code || current.content?.time_code || "";
                          const match = tc.match(/\[(\d+):(\d+)(?::(\d+))?\]/);
                          if (!match) return;
                          const h = match[3] ? parseInt(match[1]) : 0;
                          const m = match[3] ? parseInt(match[2]) : parseInt(match[1]);
                          const s = match[3] ? parseInt(match[3]) : parseInt(match[2]);
                          const total = h * 3600 + m * 60 + s;
                          window.open(`/dashboard/video/${current.video_id}?t=${total}`, "_blank");
                        }}
                        className="mt-2 text-[10px] text-amber-600 hover:underline inline-flex items-center gap-1"
                        data-testid="play-from-timecode"
                      >
                        {tr("▶ Lire la vidéo à ", "▶ Play the video at ")}{current.time_code || current.content?.time_code}
                      </button>
                    )}
                  </div>
                )}
                {current.content?.key_points?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">{tr("Points clés", "Key points")}</div>
                    <ul className="text-xs space-y-1">
                      {current.content.key_points.map((p, i) => (
                        <li key={i} className="flex gap-2"><span className="text-[#002FA7]">•</span> {p}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {current.source_question && (
                  <div className="pt-3 border-t border-border">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-amber-600 mb-1">
                      {tr("Question utilisateur d'origine (mal notée 👎)", "Original user question (poorly rated 👎)")}
                    </div>
                    <p className="text-xs italic text-muted-foreground">« {current.source_question} »</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Button
              onClick={() => decide("reject")}
              disabled={busy}
              variant="outline"
              size="lg"
              className="gap-2 border-red-500/40 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 min-w-[140px]"
              data-testid="btn-reject"
            >
              <XCircle size={20} weight="fill" />
              {tr("Rejeter", "Reject")}
            </Button>
            {editing ? (
              <Button
                onClick={() => decide("approve")}
                disabled={busy || !editedAnswer.trim() || !editedQuestion.trim()}
                size="lg"
                className="gap-2 bg-teal-600 hover:bg-teal-700 text-white min-w-[180px]"
                data-testid="btn-approve-edited"
              >
                <CheckCircle size={20} weight="fill" />
                {tr("Approuver (édité)", "Approve (edited)")}
              </Button>
            ) : (
              <Button
                onClick={() => decide("approve")}
                disabled={busy}
                size="lg"
                className="gap-2 bg-teal-600 hover:bg-teal-700 text-white min-w-[140px]"
                data-testid="btn-approve"
              >
                <CheckCircle size={20} weight="fill" />
                {tr("Approuver", "Approve")}
              </Button>
            )}
            {editing && (
              <Button
                onClick={() => { setEditing(false); setEditedTitle(""); setEditedAnswer(""); setEditedQuestion(""); }}
                variant="ghost"
                size="lg"
                data-testid="btn-cancel-edit"
              >
                {tr("Annuler", "Cancel")}
              </Button>
            )}
          </div>

          {/* Keyboard hints */}
          <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1.5">
              <Keyboard size={12} /> {tr("Raccourcis :", "Shortcuts:")}
            </span>
            <kbd className="px-2 py-0.5 bg-secondary rounded text-[10px] flex items-center gap-1">
              <ArrowLeft size={10} /> {tr("Rejeter", "Reject")}
            </kbd>
            <kbd className="px-2 py-0.5 bg-secondary rounded text-[10px] flex items-center gap-1">
              <ArrowRight size={10} /> {tr("Approuver", "Approve")}
            </kbd>
            <kbd className="px-2 py-0.5 bg-secondary rounded text-[10px]">{tr("E Éditer Titre/Q/R", "E Edit title/Q/A")}</kbd>
            <span>{tr("· Glisser ⇄ pour balayer", "· Drag ⇄ to swipe")}</span>
          </div>
        </>
      ) : null}

      {/* Vincent 20/05/2026 — Modal d'édition unifié (même UI que Validation des données).
          Quand l'utilisateur enregistre, on recharge la file pour refléter les modifs. */}
      {editorModalOpen && current && (
        <KnowledgeDetailModal
          unit={current}
          videoTitle={current.video_title || ""}
          formatTimestamp={(s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`}
          onClose={() => setEditorModalOpen(false)}
          isAdmin={true}
          onUpdated={(updated) => {
            // Met à jour la draft courante en place pour refléter immédiatement
            // le nouveau titre/question/réponse sans re-fetch complet.
            setDrafts((arr) => arr.map((d) => (d.id === current.id ? { ...d, ...updated } : d)));
          }}
        />
      )}
    </div>
  );
}
